import type { AppState, Detail } from './types.ts';
import { App } from './app.ts';
import { loadManifest, defaultExport } from './data/manifest.ts';
import { defaultsFor, TILES_DEFAULT, VIEW3D_DEFAULT } from './state/defaults.ts';
import { PREF, safeGet, safeSet } from './state/prefs.ts';
import { GRADIENT_PRESETS } from './gradient/presets.ts';
import { Canvas2dRenderer } from './render/canvas2d-renderer.ts';
import { WebGLRenderer } from './render/gl/webgl-renderer.ts';
import type { Renderer } from './render/renderer.ts';
import { $ } from './ui/dom.ts';
import { makeToast } from './ui/toast.ts';
import { mountPanel } from './ui/panel.ts';
import { mountGradientEditor } from './ui/gradient-editor.ts';
import { mountReadout } from './ui/readout.ts';
import { mountCapture } from './ui/capture.ts';
import { mountPointer } from './input/pointer.ts';
import { mountKeyboard } from './input/keyboard.ts';
import type { Manifest } from './types.ts';
import { applyPatchToState, decodeUrlState, viewToCamera } from './state/url-state.ts';
import { mountUrlSync } from './ui/share.ts';
import { mountTheme, themeCanvasBg } from './ui/theme.ts';
import { mountDock } from './ui/dock.ts';
import { mountFocus } from './ui/focus.ts';
import { mountAxis } from './ui/axis.ts';
import { CURVE_DISPLAY } from './ui/curve-meta.ts';

function initialState(manifest: Manifest, themeBg: string): AppState {
  // Open on the first curve of the display order (Hilbert) when it exists.
  const first = CURVE_DISPLAY.map((d) => manifest.curves.find((c) => c.id === d.id)).find((c) => !!c);
  const curve = first ?? manifest.curves[0];
  const d = defaultsFor(curve.id);
  const detailPref = safeGet(PREF.detail, 'auto');
  const shotPref = safeGet(PREF.shotScale, '2');
  return {
    curve: curve.id,
    exportId: defaultExport(curve).id,
    colorMode: d.colorMode ?? 'roygbiv',
    grad: {
      preset: d.preset ?? 'roygbiv',
      stops: (d.stops ?? GRADIENT_PRESETS[d.preset ?? 'roygbiv']).slice(),
      reverse: !!d.reverse,
      closure: d.closure ?? 'mirror',
      rotation: d.rotation ?? 0,
      repeat: d.repeat ?? 1,
      pingpong: d.pingpong !== false,
      hard: !!d.hard,
      autoCycles: !!d.autoCycles,
    },
    bgHex: safeGet(PREF.bgHex, themeBg),
    solidHex: safeGet(PREF.solidHex, d.solidHex ?? '#000000'),
    width: parseFloat(safeGet(PREF.width, String(d.width ?? 1.2))) || 1.2,
    aspect: d.aspect ?? 0,
    detail: detailPref === 'auto' ? 'auto' : ((parseInt(detailPref, 10) || 1) as Detail),
    shot: { scale: shotPref === 'max' ? 'max' : parseFloat(shotPref) || 2, transparent: false, fullDetail: true },
    // Small screens start with the canvas alone; a tap brings the dock back, G everything.
    uiHidden: typeof matchMedia === 'function' && matchMedia('(max-width: 720px)').matches,
    plate: null,
    tiles: { ...TILES_DEFAULT },
    view3d: { ...VIEW3D_DEFAULT },
    endpoints: false,
  };
}

async function boot(): Promise<void> {
  const theme = mountTheme();
  const toast = makeToast($('toast'));
  let manifest: Manifest;
  try {
    manifest = await loadManifest();
  } catch (err) {
    $('lblInfo').textContent = 'Could not load the curve manifest: ' + (err instanceof Error ? err.message : String(err));
    return;
  }
  const canvas = $<HTMLCanvasElement>('cv');
  const forceCanvas = new URLSearchParams(location.search).get('renderer') === 'canvas2d';
  let renderer: Renderer | null = null;
  let glError = '';
  if (!forceCanvas) {
    const res = WebGLRenderer.create(canvas);
    if ('renderer' in res) { renderer = res.renderer; console.info('WebGL2 renderer on', res.renderer.gpuInfo); }
    else { glError = res.error; console.warn('WebGL2 unavailable:', glError); }
  }
  if (!renderer) {
    // A canvas that failed to give a WebGL2 context cannot hand out a 2D one; swap it.
    const fresh = canvas.cloneNode(false) as HTMLCanvasElement;
    canvas.replaceWith(fresh);
    renderer = new Canvas2dRenderer(fresh);
  }
  const fromUrl = decodeUrlState(location.search, manifest);
  const app = new App(manifest, renderer, applyPatchToState(initialState(manifest, themeCanvasBg(theme.current())), fromUrl, manifest));
  app.toast = toast;
  app.themeBg = themeCanvasBg(theme.current());
  // A canvas still on the old theme's default background follows the theme;
  // a background the user picked stays.
  theme.onChange = (t, prev) => {
    app.themeBg = themeCanvasBg(t);
    if (app.state.bgHex.toLowerCase() === themeCanvasBg(prev)) {
      safeSet(PREF.bgHex, app.themeBg);
      app.store.patch({ bgHex: app.themeBg });
    }
  };
  if (renderer instanceof WebGLRenderer) {
    renderer.onContextRestored = () => { if (app.geom) renderer!.setGeometry(app.geom); app.requestDraw(); };
  } else if (!forceCanvas) {
    toast('WebGL2 unavailable - using the Canvas renderer. ' + glError, 9000);
  }

  mountPanel(app);
  mountGradientEditor(app);
  mountReadout(app);
  mountDock(app);
  mountFocus(app);
  mountAxis(app);
  const cap = mountCapture(app);
  mountPointer(app, cap);
  mountKeyboard(app, cap);
  // The canvas's own size drives the viewport: this also covers the panel
  // toggling, phone rotation and mobile address bars showing or hiding.
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(() => app.resize()).observe(renderer.canvas);
  else window.addEventListener('resize', () => app.resize());

  app.resize();
  app.selectExport();
  if (fromUrl.view) {
    Object.assign(app.cam, viewToCamera(fromUrl.view, app.viewW, app.viewH));
    app.requestDraw();
  }
  mountUrlSync(app);
  // Debug and test handle (read-only use); not part of the public surface.
  (window as unknown as { __curveApp?: App }).__curveApp = app;
}

void boot();
